// Reviewing a board scan: the user can set the market of a whole section of the board (when none was shown, when the
// server worked it out from the prices, or when the printed one looks wrong), and the results open with plain notices
// about anything shaky in the read.
import { BoardScanLine, SimDriverResult, SimulationRun } from '../services/api';

export type PricedMarket = 'win' | 'top3' | 'top5' | 'top10';
export const PRICED_MARKETS: PricedMarket[] = ['win', 'top3', 'top5', 'top10'];
export const isPricedMarket = (m: string): m is PricedMarket => (PRICED_MARKETS as string[]).includes(m);

const PROB: Record<PricedMarket, keyof SimDriverResult> = {
  win: 'winProb',
  top3: 'top3Prob',
  top5: 'top5Prob',
  top10: 'top10Prob',
};

/** Group → the market the user picked for it. */
export type MarketPicks = Record<string, PricedMarket>;

/** The lines with the user's market picks applied: each picked section gets that market, our probabilities for it,
 * and a status from the two reads again (agreeing reads price it; one read or a disagreement still doesn't). */
export const applyMarketPicks = (
  lines: BoardScanLine[],
  picks: MarketPicks,
  run: Pick<SimulationRun, 'drivers' | 'layered'>,
): BoardScanLine[] => {
  if (Object.keys(picks).length === 0) return lines;
  const base = new Map(run.drivers.map((d) => [d.driver, d]));
  const layered = new Map((run.layered?.drivers ?? []).map((d) => [d.driver, d]));
  return lines.map((l) => {
    const m = l.group ? picks[l.group] : undefined;
    if (!m || !l.driver) return l;
    // only lines held back for their market are re-judged; a bad price or an unmatched driver stays as it was
    const marketHeld =
      l.status === 'ok' || l.status === 'unclear' || (l.status === 'suspect' && !!l.candidates?.length);
    const status: BoardScanLine['status'] = !marketHeld
      ? l.status
      : l.oddsRead2 === undefined
        ? 'unverified'
        : l.oddsRead2 === l.odds
          ? 'ok'
          : 'disagree';
    const reason =
      status === 'ok'
        ? undefined
        : status === 'unverified'
          ? 'only one of the two reads saw this line'
          : status === 'disagree'
            ? 'the two reads disagree on this price'
            : l.reason;
    const pb = base.get(l.driver)?.[PROB[m]];
    const pl = layered.get(l.driver)?.[PROB[m]];
    return {
      ...l,
      market: m,
      marketBasis: 'picked',
      status,
      reason,
      probBase: typeof pb === 'number' ? pb : undefined,
      probLayered: typeof pl === 'number' ? pl : undefined,
    };
  });
};

export interface SectionNotice {
  group: string;
  /** inferred: no market shown, worked out from the prices; cautious: no market shown and two fit, priced as the
   * narrower; mismatch: the printed market looks wrong, priced as the narrower of the two; unclear: couldn't tell
   * (held back); picked: set by the user. */
  kind: 'inferred' | 'cautious' | 'mismatch' | 'unclear' | 'picked';
  /** The market the section is priced as (none while unclear). */
  market?: PricedMarket;
  /** The other market it might be (cautious, mismatch). */
  other?: PricedMarket;
  /** The printed market, for a mismatch. */
  labeled?: PricedMarket;
  /** Markets to suggest first in the chips. */
  suggested: PricedMarket[];
  n: number;
  /** A few drivers in the section, to tell sections apart. */
  drivers: string[];
}

export interface ScanReview {
  sections: SectionNotice[];
  /** Lines priced (after picks). */
  priced: number;
  /** Prices the two reads didn't agree on (or only one saw). */
  misread: number;
  /** Misreads that are a sizable share of the board. */
  hardToRead?: { n: number; of: number };
  /** Most drivers aren't in this race: probably a board for another race. */
  wrongRace?: { n: number; of: number };
  /** Nothing usable was read at all. */
  nothingRead: boolean;
  /** Anything here worth a heads-up (and a reminder of the screenshot tips). */
  shaky: boolean;
  /** Something only the user can settle (a section held back). */
  needsYou: boolean;
}

const lastName = (d: string) =>
  d
    .replace(/,? (Jr|Sr|II|III)\.?$/, '')
    .split(' ')
    .pop() ?? d;

/** What to tell the user about a scan, from the server's lines (before picks), the user's picks, and the lines with
 * the picks applied. */
export const reviewScan = (
  lines: BoardScanLine[],
  picks: MarketPicks,
  applied: BoardScanLine[] = lines,
): ScanReview => {
  const byGroup = new Map<string, BoardScanLine[]>();
  for (const l of lines) {
    if (!l.group) continue;
    byGroup.set(l.group, [...(byGroup.get(l.group) ?? []), l]);
  }
  const sections: SectionNotice[] = [];
  byGroup.forEach((ls, group) => {
    const drivers = Array.from(new Set(ls.map((l) => lastName(l.driver ?? l.driverRaw)))).slice(0, 3);
    const n = ls.length;
    const cands = (ls.find((l) => l.candidates?.length)?.candidates ?? []).filter(isPricedMarket);
    const first = ls.find((l) => isPricedMarket(l.market));
    const market = first && isPricedMarket(first.market) ? first.market : undefined;
    const other = cands.find((m) => m !== market);
    if (picks[group]) {
      sections.push({ group, kind: 'picked', market: picks[group], suggested: [], n, drivers });
    } else if (ls.some((l) => l.status === 'unclear')) {
      sections.push({ group, kind: 'unclear', suggested: cands, n, drivers });
    } else if (ls.some((l) => l.marketBasis === 'cautious' && l.printedMarket)) {
      const printed = ls.find((l) => l.printedMarket)!.printedMarket!;
      sections.push({
        group,
        kind: 'mismatch',
        market,
        other,
        labeled: isPricedMarket(printed) ? printed : undefined,
        suggested: cands,
        n,
        drivers,
      });
    } else if (ls.some((l) => l.marketBasis === 'cautious')) {
      sections.push({ group, kind: 'cautious', market, other, suggested: cands, n, drivers });
    } else if (ls.some((l) => l.marketBasis === 'inferred') && market) {
      sections.push({ group, kind: 'inferred', market, suggested: [], n, drivers });
    }
  });

  // Lines on markets we price (or might, once picked), from drivers in this race.
  const relevant = lines.filter((l) => l.driver && (isPricedMarket(l.market) || l.market === 'unknown'));
  const misread = relevant.filter((l) => l.status === 'disagree' || l.status === 'unverified').length;
  const hardToRead = misread >= 3 && misread >= 0.2 * relevant.length ? { n: misread, of: relevant.length } : undefined;
  const named = lines.filter((l) => l.market !== 'h2h' && l.market !== 'other');
  const unmatched = named.filter((l) => l.status === 'unmatched').length;
  const wrongRace = unmatched >= 3 && unmatched >= 0.5 * named.length ? { n: unmatched, of: named.length } : undefined;
  const nothingRead = lines.length === 0;
  const priced = applied.filter((l) => l.status === 'ok').length;
  const needsYou = sections.some((s) => s.kind === 'unclear');
  const shaky = nothingRead || !!hardToRead || !!wrongRace || sections.some((s) => s.kind !== 'picked');
  return { sections, priced, misread, hardToRead, wrongRace, nothingRead, shaky, needsYou };
};
